import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Box, Typography, Button, IconButton, Badge, Pagination, LinearProgress } from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TakeActionModal from './TakeActionComponent/TakeActionModal';
import { fetchBills, fetchPolicyAreas } from '../../utils/utils';
import { getVerifiedVotesOnly, setVerifiedVotesOnly as persistVerifiedVotesOnly } from '../../utils/billVotePreferences';
import { fetchOpenStatesBills, fetchNYBills, STATE_OPTIONS } from '../../utils/stateLegislationApi';
import { POLICY_NAV_LABEL } from '../../constants/policyLabels';
import { membersFromNavState, hasMemberNavState } from '../../utils/billsMemberFilter';
import { useUser } from '../../context/UserContext';
import BillsSearchBar from './BillsSearchBar';
import BillCard from './BillCard';
import BillsFilterModal from './BillsFilterModal';
import PolicyViewTabs, { POLICY_VIEW_LEGISLATION, POLICY_VIEW_BALLOT } from './PolicyViewTabs';
import BallotMeasuresPanel from './BallotMeasuresPanel';
import { buildBallotListSearchParams, BALLOT_SCOPE_DEFAULT } from '../../utils/ballotMeasuresApi';

import './BillsTable.scss';

const PAGE_SIZE = 15;
const DEFAULT_BILL_SORT = 'most_liked';

function normalizeBillSort(sort) {
  if (sort === 'most_liked' || sort === 'most_disliked') return sort;
  return DEFAULT_BILL_SORT;
}

/** @param {string | null} s */
function parseCommaParam(s) {
  if (!s || typeof s !== 'string') return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function normalizeSearchParamsForCompare(sp) {
  return [...sp.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

function encodeMembersForUrl(senators, representatives) {
  const ss = [];
  const sc = [];
  senators.forEach((m) => {
    if (m.sponsored) ss.push(m.bioguideId);
    if (m.cosponsored) sc.push(m.bioguideId);
  });
  const hs = [];
  const hc = [];
  representatives.forEach((m) => {
    if (m.sponsored) hs.push(m.bioguideId);
    if (m.cosponsored) hc.push(m.bioguideId);
  });
  return { ss: ss.join(','), sc: sc.join(','), hs: hs.join(','), hc: hc.join(',') };
}

function decodeMembersFromUrl(sSponsors, sCosponsors, nameLookup) {
  const sp = parseCommaParam(sSponsors);
  const cs = parseCommaParam(sCosponsors);
  const byId = {};
  sp.forEach((id) => {
    byId[id] = { bioguideId: id, name: nameLookup(id) || id, sponsored: true, cosponsored: false };
  });
  cs.forEach((id) => {
    if (byId[id]) byId[id].cosponsored = true;
    else byId[id] = { bioguideId: id, name: nameLookup(id) || id, sponsored: false, cosponsored: true };
  });
  return Object.values(byId);
}

/**
 * @param {string} searchWithoutQuestionMark
 * @param {(id: string) => string | null | undefined} nameLookup
 */
function parseBillsListUrl(searchWithoutQuestionMark, nameLookup) {
  const params = new URLSearchParams(searchWithoutQuestionMark || '');
  const stateCode = params.get('state')?.toUpperCase() || 'FED';
  const q = params.get('q') || '';
  const pRaw = parseInt(params.get('p'), 10);
  const page = Number.isFinite(pRaw) && pRaw > 0 ? pRaw - 1 : 0;
  const sort = normalizeBillSort(params.get('sort') || DEFAULT_BILL_SORT);
  const congressNums = parseCommaParam(params.get('congress'))
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n));
  const statusCodes = parseCommaParam(params.get('status'));
  const policyAreas = parseCommaParam(params.get('policy'));
  const chamber = params.get('chamber') || '';
  const selectedSenators = decodeMembersFromUrl(params.get('ss'), params.get('sc'), nameLookup);
  const selectedRepresentatives = decodeMembersFromUrl(params.get('hs'), params.get('hc'), nameLookup);
  return {
    stateCode,
    q,
    page,
    sort,
    congressNums,
    statusCodes,
    policyAreas,
    chamber,
    selectedSenators,
    selectedRepresentatives,
  };
}

function buildBillsListSearchParams({
  selectedState,
  appliedSearchQuery,
  page,
  sortBy,
  congressNums,
  statusCodes,
  policyAreas,
  chamber,
  selectedSenators,
  selectedRepresentatives,
  isFederal,
}) {
  const p = new URLSearchParams();
  if (selectedState && selectedState !== 'FED') p.set('state', selectedState);
  if (appliedSearchQuery) p.set('q', appliedSearchQuery);
  if (page > 0) p.set('p', String(page + 1));
  if (sortBy && sortBy !== DEFAULT_BILL_SORT) p.set('sort', sortBy);
  if (isFederal) {
    if (congressNums.length) p.set('congress', congressNums.join(','));
    if (statusCodes.length) p.set('status', statusCodes.join(','));
    if (policyAreas.length) p.set('policy', policyAreas.join(','));
    if (chamber) p.set('chamber', chamber);
    const { ss, sc, hs, hc } = encodeMembersForUrl(selectedSenators, selectedRepresentatives);
    if (ss) p.set('ss', ss);
    if (sc) p.set('sc', sc);
    if (hs) p.set('hs', hs);
    if (hc) p.set('hc', hc);
  }
  return p;
}

function readUrlSnapshot() {
  if (typeof window === 'undefined') return '';
  return (window.location.search || '').replace(/^\?/, '');
}

/**
 * Compute list state on first mount so member filters from navigation apply before the first fetch.
 */
function buildInitialBillsTableState(locationState, searchParams, userContext) {
  const snap = readUrlSnapshot();
  const nameLookup = (id) => {
    if (id === userContext.userSenOneBioguideID) return userContext.userSenOneName;
    if (id === userContext.userSenTwoBioguideID) return userContext.userSenTwoName;
    if (id === userContext.userRepsBioguideID) return userContext.userRepsName;
    return null;
  };

  const urlParsed = snap ? parseBillsListUrl(snap, nameLookup) : null;
  const urlHasMembers =
    urlParsed &&
    (urlParsed.selectedSenators.length > 0 || urlParsed.selectedRepresentatives.length > 0);

  const navMembers = membersFromNavState(locationState, userContext);
  const clearFilters = locationState?.clearFilters === true;

  let senators = [];
  let representatives = [];
  let stateCode = searchParams.get('state')?.toUpperCase() || 'FED';
  let q = locationState?.searchQuery || '';
  let page = 0;
  let sort = DEFAULT_BILL_SORT;
  let congressNums = [];
  let statusCodes = [];
  let policyAreas = [];
  let chamber = '';

  if (navMembers && clearFilters) {
    senators = navMembers.senators;
    representatives = navMembers.representatives;
    stateCode = 'FED';
    page = 0;
    sort = DEFAULT_BILL_SORT;
    congressNums = [];
    statusCodes = [];
    policyAreas = [];
    chamber = '';
  } else if (navMembers && !urlHasMembers) {
    senators = navMembers.senators;
    representatives = navMembers.representatives;
    if (urlParsed) {
      stateCode = urlParsed.stateCode;
      q = urlParsed.q || q;
      page = urlParsed.page;
      sort = normalizeBillSort(urlParsed.sort);
      congressNums = urlParsed.congressNums;
      statusCodes = urlParsed.statusCodes;
      policyAreas = urlParsed.policyAreas;
      chamber = urlParsed.chamber;
    }
  } else if (urlParsed) {
    senators = urlParsed.selectedSenators;
    representatives = urlParsed.selectedRepresentatives;
    stateCode = urlParsed.stateCode;
    q = urlParsed.q || q;
    page = urlParsed.page;
    sort = normalizeBillSort(urlParsed.sort);
    congressNums = urlParsed.congressNums;
    statusCodes = urlParsed.statusCodes;
    policyAreas = urlParsed.policyAreas;
    chamber = urlParsed.chamber;
  }

  return {
    senators,
    representatives,
    stateCode,
    q,
    page,
    sort,
    congressNums,
    statusCodes,
    policyAreas,
    chamber,
  };
}

const BillsTable = () => {
  const navigate = useNavigate();
  const userContext = useUser() || {};
  const {
    userRepsBioguideID,
    userRepsName,
    userSenOneName,
    userSenTwoName,
    userSenOneBioguideID,
    userSenTwoBioguideID,
    userState,
  } = userContext;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBallotView = searchParams.get('view') === POLICY_VIEW_BALLOT;

  const initialListState = React.useMemo(
    () => buildInitialBillsTableState(location.state, searchParams, userContext),
    // Only derive from first mount navigation — never re-sync from later location.state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [selectedSenators, setSelectedSenators] = useState(initialListState.senators);
  const [selectedRepresentatives, setSelectedRepresentatives] = useState(initialListState.representatives);
  const [draftSenators, setDraftSenators] = useState(initialListState.senators);
  const [draftRepresentatives, setDraftRepresentatives] = useState(initialListState.representatives);

  const [selectedState, setSelectedState] = useState(initialListState.stateCode);
  const [draftSelectedState, setDraftSelectedState] = useState(initialListState.stateCode);

  // Drop one-time navigation state so refresh/back use URL params only.
  useLayoutEffect(() => {
    if (!hasMemberNavState(location.state)) return;
    navigate({ pathname: '/bills', search: location.search || '' }, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [bills, setBills] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [sortBy, setSortBy] = useState(initialListState.sort);
  const [congressNums, setCongressNums] = useState(initialListState.congressNums);
  const [draftCongressNums, setDraftCongressNums] = useState(initialListState.congressNums);
  const [statusCodes, setStatusCodes] = useState(initialListState.statusCodes);
  const [draftStatusCodes, setDraftStatusCodes] = useState(initialListState.statusCodes);
  const [chamber, setChamber] = useState(initialListState.chamber);
  const [draftChamber, setDraftChamber] = useState(initialListState.chamber);
  const [policyAreas, setPolicyAreas] = useState(initialListState.policyAreas);
  const [draftPolicyAreas, setDraftPolicyAreas] = useState(initialListState.policyAreas);
  const [policyAreaOptions, setPolicyAreaOptions] = useState([]);
  const [policyAreasLoading, setPolicyAreasLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialListState.q);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(initialListState.q);

  const [page, setPage] = useState(initialListState.page);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [verifiedVotesOnly, setVerifiedVotesOnly] = useState(getVerifiedVotesOnly);
  const [draftVerifiedVotesOnly, setDraftVerifiedVotesOnly] = useState(getVerifiedVotesOnly);
  const [draftSortBy, setDraftSortBy] = useState(initialListState.sort);

  const isFederal = selectedState === 'FED';
  const selectedStateInfo = STATE_OPTIONS.find(s => s.code === selectedState) || STATE_OPTIONS[0];
  const memberCount = selectedSenators.length + selectedRepresentatives.length;
  const filterBadgeCount =
    (isFederal ? 0 : 1) +
    memberCount +
    congressNums.length +
    (chamber ? 1 : 0) +
    statusCodes.length +
    policyAreas.length +
    (verifiedVotesOnly ? 1 : 0) +
    (isFederal && sortBy !== DEFAULT_BILL_SORT ? 1 : 0);
  const hasActiveFilters = filterBadgeCount > 0;
  const showClear = searchQuery !== '' || hasActiveFilters;


  useEffect(() => {
    if (isFederal) {
      setPolicyAreasLoading(true);
      fetchPolicyAreas()
        .then(setPolicyAreaOptions)
        .catch(() => setPolicyAreaOptions([]))
        .finally(() => setPolicyAreasLoading(false));
    }
  }, [isFederal]);

  const memberNameLookup = React.useCallback((id) => {
    if (id === userSenOneBioguideID) return userSenOneName;
    if (id === userSenTwoBioguideID) return userSenTwoName;
    if (id === userRepsBioguideID) return userRepsName;
    return null;
  }, [userSenOneBioguideID, userSenOneName, userSenTwoBioguideID, userSenTwoName, userRepsBioguideID, userRepsName]);

  const handlePolicyViewChange = (nextView) => {
    if (nextView === POLICY_VIEW_BALLOT) {
      const ballotParams = buildBallotListSearchParams({
        stateCode: userState || '',
        scope: BALLOT_SCOPE_DEFAULT,
        query: '',
        page: 1,
      });
      setSearchParams(ballotParams, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Restore list filters from the query string (e.g. browser Back from bill detail).
  // useLayoutEffect so URL is applied before the persist effect runs (avoids clobbering the URL).
  useLayoutEffect(() => {
    if (isBallotView) return;
    const raw = (location.search || '').replace(/^\?/, '');
    if (!raw) return;
    const h = parseBillsListUrl(raw, memberNameLookup);
    const fed = h.stateCode === 'FED';
    setSelectedState(h.stateCode);
    setDraftSelectedState(h.stateCode);
    setAppliedSearchQuery(h.q);
    setSearchQuery(h.q);
    setPage(h.page);
    setSortBy(normalizeBillSort(h.sort));
    setCongressNums(fed ? h.congressNums : []);
    setDraftCongressNums(fed ? h.congressNums : []);
    setChamber(fed ? h.chamber : '');
    setDraftChamber(fed ? h.chamber : '');
    setStatusCodes(fed ? h.statusCodes : []);
    setDraftStatusCodes(fed ? h.statusCodes : []);
    setPolicyAreas(fed ? h.policyAreas : []);
    setDraftPolicyAreas(fed ? h.policyAreas : []);
    setSelectedSenators(fed ? h.selectedSenators : []);
    setSelectedRepresentatives(fed ? h.selectedRepresentatives : []);
    setDraftSenators(fed ? h.selectedSenators : []);
    setDraftRepresentatives(fed ? h.selectedRepresentatives : []);
  }, [location.search, memberNameLookup, isBallotView]);

  // Persist filters in the URL so Back/refresh keep the same list view.
  useEffect(() => {
    if (location.pathname !== '/bills' || isBallotView) return;
    const next = buildBillsListSearchParams({
      selectedState,
      appliedSearchQuery,
      page,
      sortBy,
      congressNums,
      chamber,
      statusCodes,
      policyAreas,
      selectedSenators,
      selectedRepresentatives,
      isFederal,
    });
    const curr = new URLSearchParams((location.search || '').replace(/^\?/, ''));
    if (normalizeSearchParamsForCompare(next) === normalizeSearchParamsForCompare(curr)) return;
    setSearchParams(next, { replace: true });
  }, [
    location.pathname,
    location.search,
    selectedState,
    appliedSearchQuery,
    page,
    sortBy,
    congressNums,
    chamber,
    statusCodes,
    policyAreas,
    selectedSenators,
    selectedRepresentatives,
    isFederal,
    isBallotView,
    setSearchParams,
  ]);


  const filterModel = React.useMemo(() => {
    if (!isFederal) return {};
    const items = [];
    if (congressNums.length > 0) items.push({ field: 'congress', value: congressNums.join(','), operator: 'in' });
    if (chamber) items.push({ field: 'origin_chamber', value: chamber, operator: 'equals' });
    if (statusCodes.length > 0) items.push({ field: 'status_code', value: statusCodes.join(','), operator: 'in' });
    if (policyAreas.length > 0) items.push({ field: 'policy_area', value: policyAreas.join(','), operator: 'in' });
    return items.length ? { items } : {};
  }, [congressNums, chamber, statusCodes, policyAreas, isFederal]);

  const sponsorIds = React.useMemo(() => {
    if (!isFederal) return [];
    const ids = [];
    selectedSenators.forEach((s) => { if (s.sponsored) ids.push(s.bioguideId); });
    selectedRepresentatives.forEach((r) => { if (r.sponsored) ids.push(r.bioguideId); });
    return ids;
  }, [selectedSenators, selectedRepresentatives, isFederal]);

  const cosponsorIds = React.useMemo(() => {
    if (!isFederal) return [];
    const ids = [];
    selectedSenators.forEach((s) => { if (s.cosponsored) ids.push(s.bioguideId); });
    selectedRepresentatives.forEach((r) => { if (r.cosponsored) ids.push(r.bioguideId); });
    return ids;
  }, [selectedSenators, selectedRepresentatives, isFederal]);

  // Main data fetch — branches on selectedState (legislation view only)
  useEffect(() => {
    if (isBallotView) return undefined;
    let isCurrent = true;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        let data;
        if (isFederal) {
          data = await fetchBills(page + 1, PAGE_SIZE, appliedSearchQuery, [], '', '', '', filterModel, sortBy, sponsorIds, cosponsorIds, false, verifiedVotesOnly);
          if (!isCurrent) return;
          setBills(data.bills || []);
          setTotalCount(data.totalCount ?? 0);
        } else if (selectedState === 'NY') {
          data = await fetchNYBills(appliedSearchQuery, page + 1);
          if (!isCurrent) return;
          if (data.error) { setError(data.error); setBills([]); setTotalCount(0); return; }
          setBills(data.bills || []);
          setTotalCount(data.totalCount ?? 0);
        } else {
          data = await fetchOpenStatesBills(selectedState, appliedSearchQuery, page + 1);
          if (!isCurrent) return;
          if (data.error) { setError(data.error); setBills([]); setTotalCount(0); return; }
          setBills(data.bills || []);
          setTotalCount(data.totalCount ?? 0);
        }
      } catch (err) {
        if (!isCurrent) return;
        setError(err.message);
      } finally {
        if (isCurrent) setLoading(false);
      }
    };
    load();
    return () => { isCurrent = false; };
  }, [page, appliedSearchQuery, selectedSenators, selectedRepresentatives, sortBy, filterModel, sponsorIds, cosponsorIds, isFederal, selectedState, verifiedVotesOnly, isBallotView]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSearch = (searchValue) => {
    setAppliedSearchQuery(searchValue);
    setPage(0);
    scrollToTop();
  };

  const handleFilterOpen = () => {
    setDraftSenators(selectedSenators);
    setDraftRepresentatives(selectedRepresentatives);
    setDraftCongressNums(congressNums);
    setDraftChamber(chamber);
    setDraftStatusCodes(statusCodes);
    setDraftPolicyAreas(policyAreas);
    setDraftSelectedState(selectedState);
    setDraftVerifiedVotesOnly(verifiedVotesOnly);
    setDraftSortBy(sortBy);
    setFilterModalOpen(true);
  };

  const handleFilterApply = () => {
    if (draftSelectedState !== selectedState) {
      setSelectedSenators([]);
      setSelectedRepresentatives([]);
      setSortBy(DEFAULT_BILL_SORT);
      setCongressNums([]);
      setChamber('');
      setStatusCodes([]);
      setPolicyAreas([]);
      setVerifiedVotesOnly(false);
      setDraftVerifiedVotesOnly(false);
      persistVerifiedVotesOnly(false);
    } else {
      setSelectedSenators(draftSenators);
      setSelectedRepresentatives(draftRepresentatives);
      setCongressNums(draftCongressNums);
      setChamber(draftChamber);
      setStatusCodes(draftStatusCodes);
      setPolicyAreas(draftPolicyAreas);
      setVerifiedVotesOnly(draftVerifiedVotesOnly);
      persistVerifiedVotesOnly(draftVerifiedVotesOnly);
      setSortBy(draftSortBy);
    }
    setSelectedState(draftSelectedState);
    setPage(0);
    setFilterModalOpen(false);
    scrollToTop();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setDraftSenators([]);
    setDraftRepresentatives([]);
    setSelectedSenators([]);
    setSelectedRepresentatives([]);
    setSortBy(DEFAULT_BILL_SORT);
    setDraftCongressNums([]);
    setCongressNums([]);
    setDraftChamber('');
    setChamber('');
    setDraftStatusCodes([]);
    setStatusCodes([]);
    setDraftPolicyAreas([]);
    setPolicyAreas([]);
    setSelectedState('FED');
    setDraftSelectedState('FED');
    setVerifiedVotesOnly(false);
    setDraftVerifiedVotesOnly(false);
    persistVerifiedVotesOnly(false);
    setPage(0);
    setFilterModalOpen(false);
    scrollToTop();
  };

  // Logged-out users get the address lookup inside TakeActionModal itself.
  const handleTakeAction = (bill) => {
    setSelectedBill(bill);
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const searchPlaceholder = isFederal
    ? `Search federal ${POLICY_NAV_LABEL.toLowerCase()}…`
    : `Search ${selectedStateInfo.name} ${POLICY_NAV_LABEL.toLowerCase()}…`;

  const activePolicyView = isBallotView ? POLICY_VIEW_BALLOT : POLICY_VIEW_LEGISLATION;

  return (
    <Box className="bills" sx={{ p: 2 }}>
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        <PolicyViewTabs activeView={activePolicyView} onViewChange={handlePolicyViewChange} />

        <Box sx={{ p: 2 }}>
      {isBallotView ? (
        <BallotMeasuresPanel userState={userState || null} />
      ) : (
        <>
      {loading && bills.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography variant="body1">Loading legislation...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200, gap: 1 }}>
          <Typography variant="body1" color="error">{error}</Typography>
          {error.includes("Rate limit") && (
            <Typography variant="body2" color="text.secondary">State API rate limits apply. Results are cached for 10 minutes.</Typography>
          )}
        </Box>
      )}

      {!error && !(loading && bills.length === 0) && (
        <>
      {/* Compact header: search + Filter button */}
      <Box className="bills-header bills-header--compact">
        <Box className="bills-header__search-wrap">
          <BillsSearchBar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearch={handleSearch}
            placeholder={searchPlaceholder}
          />
          {showClear && (
            <Button onClick={handleClearFilters} className="bills-header__clear">
              Clear Filters
            </Button>
          )}
        </Box>
        <Box className="bills-header__actions">
          <Badge
            badgeContent={filterBadgeCount}
            color="primary"
            invisible={filterBadgeCount === 0}
            className="bills-header__filter-badge"
          >
            <IconButton
              aria-label="Open filters"
              onClick={handleFilterOpen}
              className="bills-header__filter-btn bills-header__filter-btn--active"
              size="medium"
            >
              <FilterListRoundedIcon />
            </IconButton>
          </Badge>
        </Box>
      </Box>

      {/* State banner when non-federal */}
      {!isFederal && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1, py: 1, borderRadius: '8px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#000' }}>
            Showing {selectedStateInfo.name} state legislation
          </Typography>
        </Box>
      )}

      <BillsFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        draftSenators={draftSenators}
        draftRepresentatives={draftRepresentatives}
        onDraftSenatorsChange={setDraftSenators}
        onDraftRepresentativesChange={setDraftRepresentatives}
        userSenOneBioguideID={userSenOneBioguideID}
        userSenOneName={userSenOneName}
        userSenTwoBioguideID={userSenTwoBioguideID}
        userSenTwoName={userSenTwoName}
        userRepsBioguideID={userRepsBioguideID}
        userRepsName={userRepsName}
        draftCongressNums={draftCongressNums}
        onDraftCongressNumsChange={setDraftCongressNums}
        draftChamber={draftChamber}
        onDraftChamberChange={setDraftChamber}
        draftStatusCodes={draftStatusCodes}
        onDraftStatusCodesChange={setDraftStatusCodes}
        draftPolicyAreas={draftPolicyAreas}
        onDraftPolicyAreasChange={setDraftPolicyAreas}
        policyAreaOptions={policyAreaOptions}
        policyAreasLoading={policyAreasLoading}
        onApply={handleFilterApply}
        selectedState={selectedState}
        draftSelectedState={draftSelectedState}
        onDraftStateChange={setDraftSelectedState}
        userState={userState || null}
        draftVerifiedVotesOnly={draftVerifiedVotesOnly}
        onDraftVerifiedVotesOnlyChange={setDraftVerifiedVotesOnly}
        draftSortBy={draftSortBy}
        onDraftSortByChange={setDraftSortBy}
        defaultBillSort={DEFAULT_BILL_SORT}
      />

      {/* Refetch loading indicator */}
      {loading && bills.length > 0 && (
        <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
      )}

      {/* Card grid */}
      <Box className="bills-grid" sx={{ width: '100%', opacity: loading && bills.length > 0 ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>
        {bills.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No bills found. Try adjusting your search or filters.
          </Typography>
        ) : (
          bills.map((bill) => (
            <BillCard key={bill.id} bill={bill} onTakeAction={handleTakeAction} userState={userState} verifiedVotesOnly={verifiedVotesOnly} />
          ))
        )}
      </Box>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, value) => { setPage(value - 1); scrollToTop(); }}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      <TakeActionModal
        open={!!selectedBill}
        onClose={() => setSelectedBill(null)}
        billType={selectedBill?.type}
        billNumber={selectedBill?.bill_number}
        billTitle={selectedBill?.title}
        billCongress={selectedBill?.congress}
      />
        </>
      )}
        </>
      )}
        </Box>
      </Box>
    </Box>
  );
};

export default BillsTable;
